import { useState, useEffect } from 'react';
import { supabase, Review } from '../../../lib/supabase';
import { mockReviews, MockReview } from '../../../mocks/reviews';

type DisplayReview = {
  id: number;
  author_name: string;
  author_image?: string;
  pet_name?: string | null;
  city?: string | null;
  rating: number;
  content: string;
};

export default function ReviewsSection() {
  const [reviews, setReviews] = useState<DisplayReview[]>([]);

  useEffect(() => {
    const fetchReviews = async () => {
      const { data } = await supabase
        .from('reviews')
        .select('*')
        .eq('is_published', true)
        .order('created_at', { ascending: false })
        .limit(6);

      if (data && (data as Review[]).length > 0) {
        setReviews(data as Review[]);
      } else {
        // Supabase未接続時はモックデータを使用
        const fallback: DisplayReview[] = mockReviews
          .filter((r: MockReview) => r.is_published)
          .slice(0, 6)
          .map((r: MockReview) => ({
            id: r.id,
            author_name: r.author_name,
            author_image: r.author_image,
            pet_name: r.pet_name,
            city: r.city,
            rating: r.rating,
            content: r.content,
          }));
        setReviews(fallback);
      }
    };
    fetchReviews();
  }, []);

  if (reviews.length === 0) return null;

  return (
    <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-12 md:mb-16">
          <p className="text-xs font-semibold text-[#81D8D0] tracking-widest uppercase mb-3 sm:mb-4">REVIEWS</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1B2A4A]">お客様の声</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {reviews.map((review) => (
            <div
              key={review.id}
              className="bg-[#EEF4F7] rounded-2xl p-6 flex flex-col"
            >
              <div className="flex items-center gap-0.5 mb-4">
                {[1, 2, 3, 4, 5].map((s) => (
                  <i
                    key={s}
                    className={`text-sm ${s <= review.rating ? 'ri-star-fill text-yellow-400' : 'ri-star-line text-gray-300'}`}
                  ></i>
                ))}
              </div>
              <p className="text-sm text-[#1B2A4A]/70 leading-relaxed flex-1 mb-5">
                &ldquo;{review.content}&rdquo;
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex-shrink-0 bg-[#81D8D0]/20 rounded-full flex items-center justify-center overflow-hidden">
                  {review.author_image ? (
                    <img
                      src={review.author_image}
                      alt={review.author_name}
                      className="w-full h-full object-cover object-top"
                    />
                  ) : (
                    <i className="ri-user-3-line text-lg text-[#81D8D0]"></i>
                  )}
                </div>
                <div>
                  <p className="text-sm font-bold text-[#1B2A4A]">
                    {review.author_name}
                    {review.pet_name && (
                      <span className="text-xs font-normal text-[#1B2A4A]/50 ml-1">
                        （{review.pet_name}のオーナー）
                      </span>
                    )}
                  </p>
                  {review.city && (
                    <p className="flex items-center gap-0.5 text-xs text-[#1B2A4A]/50 mt-0.5">
                      <i className="ri-map-pin-line text-[#81D8D0]"></i>
                      {review.city}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
